import { t } from "i18next";
import { Button, Form, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { StaffTeamSeason } from "../../../../interfaces/staff";
import { createStaffTeamSeason, deleteStaffTeamSeason, getStaffTeamSeasons } from "../../../../services/staff";
import { Media } from "../../../../interfaces/media";
import DataTable from "react-data-table-component";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { Club } from "../../../../interfaces/club";
import { Team } from "../../../../interfaces/team";
import { getClubOptions } from "../../../../services/club";
import { getSeasonOptions } from "../../../../services/season";
import { Season } from "../../../../interfaces/season";
import { getTeamOptions } from "../../../../services/team";
import { tableStyles, getPaginationOptions } from "../../../../util/data-table";

interface AdminStaffTeamSeasonProps {
    id: number;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

export function AdminStaffTeamSeason(props: AdminStaffTeamSeasonProps) {
    const [loaded, setLoaded] = useState(false);
    const [clubOptions, setClubOptions] = useState<Club[]>([]);
    const [teamOptions, setTeamOptions] = useState<Team[]>([]);
    const [seasonOptions, setSeasonOptions] = useState<Season[]>([]);
    const [staffTeamSeasons, setStaffTeamSeasons] = useState<StaffTeamSeason[]>([]);
    const [showAdd, setShowAdd] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        Promise.all([
            getClubOptions(),
            getSeasonOptions(),
            getStaffTeamSeasons(props.id)
        ]).then((response: any) => {
            setClubOptions(response[0].data);
            setSeasonOptions(response[1].data);
            setStaffTeamSeasons(response[2].data);
            setLoaded(true);
        });
    }, []);

    const handleSave = (event: any) => {
        event.preventDefault();

        const body = {
            staff_id: props.id,
            team_id: event.target.team_id.value,
            season_id: event.target.season_id.value
        }
        createStaffTeamSeason(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    getStaffTeamSeasons(props.id)
                        .then(response => {
                            setStaffTeamSeasons(response.data);
                        });
                    setShowAdd(false);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    const handleClose = () => {
        props.onClose();
    }

    const handleShowDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();

        if (!confirmDelete) {
            setShowDelete(false);
            setSelectedId(0);
            return;
        }

        deleteStaffTeamSeason(event.target.id.value)
            .then((response: any) => {
                getStaffTeamSeasons(props.id)
                    .then(response => {
                        setStaffTeamSeasons(response.data);
                    });
                setShowDelete(false);
                setSelectedId(0);
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(true);
    }

    const handleCloseAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(false);
    }


    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleShowDelete } data-id={ row.row.id }>
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

    const columns = [
        {
            id: 'id',
            name: '#',
            style: { maxWidth: '80px' },
            selector: (row: any) => row.id,
            sortable: true
        },
        {
            id: 'season_name',
            name: t('season'),
            selector: (row: any) => row.season_name,
            sortable: true
        },
        {
            id: 'club_name',
            name: t('club'),
            selector: (row: any) => row.club_name,
            sortable: true
        },
        {
            id: 'team_name',
            name: t('team'),
            selector: (row: any) => row.team_name,
            sortable: true
        },
        {
            id: 'actions',
            name: t("actions"),
            cell: (row: any) => <CustomActions row={row} />,
        }

    ];

    const handleClubChange = (event: any) => {
        const club = event.target.value;
        getTeamOptions(club)
            .then(response => {
                setTeamOptions(response.data);
            });
    }

    return (
        <Modal className="admin-staff-team-season-container" show={ true }
            onHide={ handleClose } size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("staff_team_seasons") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded &&
                    <LoadingSpinner />
                }
                { loaded &&
                    <div className="staff-team-seasons-container">
                        <div className="subtable-container">
                            <DataTable
                                columns={ columns }
                                data={staffTeamSeasons}
                                responsive={ true }
                                striped={ true }
                                highlightOnHover={ true }
                                defaultSortFieldId={ 'team-season_date' }
                                defaultSortAsc={ false }
                                pagination={ true }
                                customStyles={ tableStyles }
                                paginationComponentOptions={ getPaginationOptions() }
                            />
                        </div>
                        { !showAdd && !showDelete &&
                            <a className="team-season-add-new" href="#" onClick={ handleAddNew }>
                                <i className="bi bi-plus-circle"></i> { t("add") }
                            </a>
                        }
                        { showAdd &&
                            <Form onSubmit={ handleSave } className="add-team-season">
                                <a href="#" className="close" onClick={ handleCloseAddNew }>
                                </a>
                                <Form.Control type="hidden" name="id" required defaultValue={ props.id } />
                                <div className="fields">
                                    <Form.Group className="mb-2" controlId="season_id">
                                        <Form.Label aria-required="true">{ t("season") }</Form.Label>
                                        <Form.Select aria-label={ t("seasons") } name="season_id" required>
                                            <option value="">{ t("select_season") }</option>
                                            { seasonOptions.map((s: Season) => {
                                                return <option key={ 'season-' + s.id }
                                                    value={ s.id }>{ s.name }</option>
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2" controlId="club_id">
                                        <Form.Label aria-required="true">{ t("club") }</Form.Label>
                                        <Form.Select aria-label={ t("clubs") } name="club_id" required onChange={ handleClubChange }>
                                            <option value="">{ t("select_club") }</option>
                                            { clubOptions.map((c: Club) => {
                                                return <option key={ 'club-' + c.id }
                                                    value={ c.id }>{ c.name }</option>
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2" controlId="team_id">
                                        <Form.Label aria-required="true">{ t("team") }</Form.Label>
                                        <Form.Select aria-label={ t("teams") } name="team_id" required>
                                            <option value="">{ t("select_team") }</option>
                                            { teamOptions.map((t: Team) => {
                                                return <option key={ 'team-' + t.id }
                                                    value={ t.id }>{ t.name }</option>
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">
                                        { t("add") }
                                    </Button>
                                </div>
                            </Form>
                        }
                        { showDelete &&
                            <Form onSubmit={ handleDelete } className="delete-staff">
                                <Form.Control type="hidden" name="id" required defaultValue={ selectedId } />
                                <p>
                                    { t("delete_sts_message") }
                                </p>
                                <div className="actions">
                                    <Button type="submit" variant="primary" value="yes" onClick={ () => { setConfirmDelete(true) } }>
                                        { t("Yes") }
                                    </Button>
                                    <Button type="submit" variant="secondary" value="no" onClick={ () => { setConfirmDelete(false) } }>
                                        { t("No") }
                                    </Button>
                                </div>
                            </Form>
                        }
                    </div>
                }
            </Modal.Body>
        </Modal>
    );
}
